/**
 * Minimal DOM helpers for the HUD. The spec mandates native HTML/CSS with no framework (§3), so
 * these keep panel code declarative without pulling in a dependency.
 */

export type Child = Node | string | null | undefined | false;

export interface ElementOptions {
  class?: string;
  text?: string;
  title?: string;
  html?: string;
  attrs?: Record<string, string | number | boolean | undefined>;
  dataset?: Record<string, string | undefined>;
  style?: Partial<CSSStyleDeclaration>;
  on?: Partial<{ [K in keyof HTMLElementEventMap]: (event: HTMLElementEventMap[K]) => void }>;
}

/** Create an element with attributes, listeners, and children in one call. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElementOptions = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.class) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.html !== undefined) node.innerHTML = options.html;
  if (options.title !== undefined) node.title = options.title;

  for (const [key, value] of Object.entries(options.attrs ?? {})) {
    if (value === undefined || value === false) continue;
    node.setAttribute(key, value === true ? '' : String(value));
  }
  for (const [key, value] of Object.entries(options.dataset ?? {})) {
    if (value !== undefined) node.dataset[key] = value;
  }
  if (options.style) Object.assign(node.style, options.style);
  for (const [type, listener] of Object.entries(options.on ?? {})) {
    node.addEventListener(type, listener as EventListener);
  }

  append(node, ...children);
  return node;
}

/** Append children, skipping nullish/false entries so conditional rendering reads cleanly. */
export function append(parent: Node, ...children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
}

/** Remove every child of a node. */
export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Add or remove a class based on a boolean. */
export function setClass(node: Element, className: string, on: boolean): void {
  node.classList.toggle(className, on);
}

/** Format a length in meters for display, with feet in parentheses (spec §5 — UI boundary only). */
export function formatLength(meters: number, unit: 'meters' | 'feet' = 'meters'): string {
  if (unit === 'feet') {
    const feet = meters * 3.28084;
    return feet < 10 ? `${feet.toFixed(1)} ft` : `${Math.round(feet)} ft`;
  }
  return meters < 10 ? `${meters.toFixed(1)} m` : `${Math.round(meters)} m`;
}

/**
 * Format an area for display. Whole numbers throughout: nobody reads a bed as 42.37 m², and the
 * decimal implies a precision a hand-drawn outline does not have.
 */
export function formatArea(squareMeters: number, unit: 'meters' | 'feet' = 'meters'): string {
  if (unit === 'feet') return `${Math.round(squareMeters * 10.7639)} ft²`;
  return `${squareMeters < 10 ? squareMeters.toFixed(1) : Math.round(squareMeters)} m²`;
}

/** Debounce a void-returning function by `waitMs`, returning a cancelable wrapper. */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  waitMs: number,
): ((...args: A) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const wrapped = (...args: A): void => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, waitMs);
  };
  wrapped.cancel = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  return wrapped;
}
